import { useLingui } from "@lingui/react/macro";
import {
  Button,
  Cell,
  Column,
  Disclosure,
  DisclosurePanel,
  Row,
  Table,
  TableBody,
  TableHeader,
} from "react-aria-components";
import { useNavigate, useParams } from "react-router";
import clsx from "clsx";
import {
  equipmentAssetStatusSchema,
  getAssetStatusLabel,
  operationalStatusSchema,
} from "@medoma/models/equipment";
import { humanFormat } from "@medoma/models/date-and-time";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text } from "@medoma/components/Text/Text";
import CaretRightIcon from "@medoma/components/icons/CaretRightIcon";
import type { equipmentQueryOptions } from "@/api/Equipment";
import { getPatientNameWithStatus } from "@/api/Patients";
import styles from "./EquipmentCategory.module.scss";

type ColumnId = "name" | "comment" | "status" | "patient" | "allocatedAt";

const NOWRAP_COLUMNS = new Set<ColumnId>(["name", "patient", "allocatedAt"]);

type Equipment = Awaited<
  ReturnType<ReturnType<typeof equipmentQueryOptions>["queryFn"]>
>;
type EquipmentCategoryItem = Equipment[number];
type EquipmentAsset = EquipmentCategoryItem["assets"][number];

const isActive = (asset: EquipmentAsset) =>
  asset.operationalStatus !== operationalStatusSchema.enum.inactive;

const countAvailable = (assets: EquipmentAsset[]) =>
  assets.filter(
    (a) =>
      isActive(a) && a.status === equipmentAssetStatusSchema.enum.available,
  ).length;

export const EquipmentCategory = ({
  category,
  columns,
}: {
  category: EquipmentCategoryItem;
  columns: ReadonlyArray<{
    id: ColumnId;
    name: string;
    isRowHeader?: true;
  }>;
}) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { assetId: selectedAssetId } = useParams<{ assetId: string }>();

  const total = category.assets.filter(isActive).length;
  const availableCount = countAvailable(category.assets);

  const renderCell = (asset: EquipmentAsset, columnId: ColumnId) => {
    switch (columnId) {
      case "name":
        return asset.name;
      case "comment":
        return asset.comment?.trim() ? asset.comment : "–";
      case "status":
        return t(getAssetStatusLabel(asset));
      case "patient":
        return asset.status === equipmentAssetStatusSchema.enum.allocated
          ? getPatientNameWithStatus(asset.patient, "full")
          : "–";
      case "allocatedAt":
        return asset.allocatedAt
          ? humanFormat(asset.allocatedAt, { dateStyle: "short" })
          : "–";
    }
  };

  return (
    <Disclosure id={category.id} className={styles.disclosure}>
      <Button
        slot="trigger"
        className={styles.disclosureHeader}
        aria-label={category.name}
      >
        <CaretRightIcon />
        <Heading level="h2" size="base">
          {category.name}
        </Heading>
        <Text element="span" color="faded">
          {availableCount}/{total}
        </Text>
      </Button>
      <DisclosurePanel>
        <Table className={styles.assetsTable} aria-label={category.name}>
          <TableHeader columns={columns}>
            {(column) => (
              <Column
                id={column.id}
                isRowHeader={column.isRowHeader}
                className={
                  NOWRAP_COLUMNS.has(column.id) ? styles.nowrap : undefined
                }
              >
                {column.name}
              </Column>
            )}
          </TableHeader>
          <TableBody
            items={category.assets}
            dependencies={[selectedAssetId]}
            renderEmptyState={() => (
              <div className={styles.tableEmptyState}>
                {t`Ingen utrustning i denna kategori.`}
              </div>
            )}
          >
            {(item) => (
              <Row
                id={item.id}
                columns={columns}
                className={clsx(
                  styles.row,
                  !isActive(item) && styles.inactiveRow,
                  item.id === selectedAssetId && styles.activeRow,
                )}
                onAction={() => navigate(item.id)}
              >
                {(column) => (
                  <Cell
                    className={
                      NOWRAP_COLUMNS.has(column.id) ? styles.nowrap : undefined
                    }
                  >
                    {renderCell(item, column.id)}
                  </Cell>
                )}
              </Row>
            )}
          </TableBody>
        </Table>
      </DisclosurePanel>
    </Disclosure>
  );
};
